import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'WordpressIntegrations',
  data () {
    return {
      isSaving: false,
      isDeleting: false,
      isSavingSettings: false,
      wordpressApiKey: '',
      wordpressApiKeyRules: [
        value => !!(value && value.trim()) || 'API key is required.'
      ],
      settings: {
        wpDomain: '',
        wpAuthorId: '',
        wpCategory: '',
        wpUrlRewriteFrom: '',
        wpUrlRewriteTo: '',
        wpRemoveFeaturedImage: false,
        wpOverwriteCategoriesOnUpdate: false,
        wpAutoPublish: false
      }
    }
  },
  computed: {
    ...mapGetters([
      'wordpressIntegration'
    ]),
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    hasExistingIntegration () {
      return !!this.wordpressIntegration
    },
    integrationSummary () {
      if (!this.wordpressIntegration) return null
      const { site_url: siteUrl, wp_domain: wpDomain, type } = this.wordpressIntegration
      return {
        type,
        site: siteUrl || wpDomain || null
      }
    }
  },
  watch: {
    wordpressIntegration: {
      immediate: true,
      handler (integration) {
        if (!integration) return
        const urlRewrite = integration.wp_url_rewrite
          ? (typeof integration.wp_url_rewrite === 'string'
              ? JSON.parse(integration.wp_url_rewrite)
              : integration.wp_url_rewrite)
          : {}
        this.settings = {
          wpDomain: integration.wp_domain || '',
          wpAuthorId: integration.wp_author_id || '',
          wpCategory: integration.wp_category || '',
          wpUrlRewriteFrom: urlRewrite.from || '',
          wpUrlRewriteTo: urlRewrite.to || '',
          wpRemoveFeaturedImage: !!integration.wp_remove_featured_image,
          wpOverwriteCategoriesOnUpdate: !!integration.wp_overwrite_categories_on_update,
          wpAutoPublish: !!integration.wp_auto_publish
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'getIntegrations',
      'postWordpressIntegration',
      'deleteIntegration'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    async saveIntegration () {
      if (!this.$refs.form?.validate()) {
        this.setSnackbar({ open: true, text: 'Please paste the API key from the ContentGecko Connector plugin.', type: 'warning' })
        return
      }

      const apiKey = this.wordpressApiKey.trim()
      this.isSaving = true

      try {
        await this.postWordpressIntegration({
          contentplanId: this.contentplanId,
          type: 'wp-plugin',
          apiKey
        })

        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.wordpressApiKey = ''
        this.setSnackbar({ open: true, text: 'WordPress plugin connected successfully!', type: 'success' })
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message || 'Could not save integration'}`,
          type: 'error'
        })
      } finally {
        this.isSaving = false
      }
    },
    async saveSettings () {
      this.isSavingSettings = true
      try {
        await this.postWordpressIntegration({
          contentplanId: this.contentplanId,
          ...this.settings
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Settings saved.', type: 'success' })
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message || 'Could not save settings'}`,
          type: 'error'
        })
      } finally {
        this.isSavingSettings = false
      }
    },
    async handleDelete () {
      if (!this.hasExistingIntegration || this.isDeleting) return
      if (!window.confirm('are you sure you want to remove integration')) return

      this.isDeleting = true
      try {
        await this.deleteIntegration({
          contentplanId: this.contentplanId,
          integrationType: 'wordpress'
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Integration removed.', type: 'success' })
        this.$router.push({
          name: 'ContentplanSettings',
          params: { contentplanId: this.contentplanId, workspaceId: this.$route.params.workspaceId }
        })
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message || 'Could not remove integration'}`,
          type: 'error'
        })
      } finally {
        this.isDeleting = false
      }
    }
  },
  async mounted () {
    try {
      await this.getIntegrations({ contentplanId: this.contentplanId })
    } catch (error) {
      this.setSnackbar({
        open: true,
        text: `Failed to load integrations: ${error?.response?.data?.message || error.message || 'Unknown error'}`,
        type: 'error'
      })
    }
  }
}
